import { z } from 'zod';

export const nullishString = ({ max }: { max?: number } = {}) =>
  z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    (typeof max === 'number' ? z.string().max(max) : z.string())
      .nullish()
      .transform((val) => val || null),
  );

const NullishString = nullishString();

const OptionalBoolean = z.preprocess(
  (val) => (typeof val === 'string' ? val.trim() : val),
  z
    .union([
      z.literal('Y'),
      z.literal('y'),
      z.literal('N'),
      z.literal('n'),
      z.literal(''),
    ])
    .nullable()
    .optional()
    .transform((value) =>
      value === 'Y' || value === 'y'
        ? true
        : value === 'N' || value === 'n'
          ? false
          : undefined,
    ),
);

export const BulkDonorScheduleRowSchema = z.object({
  accountCode: z.string().min(1),
  billAccountCode: NullishString,
  donorBreed: z.string().min(1),
  donorNameToMatch: z.string().min(1).max(41),
  donorRegistrationNumber: nullishString({ max: 20 }),
  pedigreeName: nullishString({ max: 50 }),
  pregnant: OptionalBoolean,
  birthDate: NullishString,
  programName: nullishString({ max: 100 }),
  tpi: z.number().nullish(),
  nm: z.number().nullish(),
  opuProgram: NullishString,
  tattoo: nullishString({ max: 11 }),
  received: NullishString,
  yardCode: NullishString,
  sire: NullishString,
  semen: NullishString,
  sireBreed: NullishString,
  sireRegistrationNumber: NullishString,
  collectionCode: NullishString,
  semenType: NullishString,
  donorLocation: nullishString({ max: 100 }),
  scheduleCode: NullishString,
  pool: OptionalBoolean,
  aspirationComment: nullishString({ max: 100 }),
  naab: nullishString({ max: 10 }),
  cane: nullishString({ max: 30 }),
  ship: NullishString,
  shipmentGroup: NullishString,
  freeze: NullishString,
  freezeMethod: NullishString,
  freezeInstructions: nullishString({ max: 100 }),
  export: NullishString,
  trypsinTreat: OptionalBoolean,
  nonEuCountry: NullishString,
  updateExistingDonor: z.boolean().nullish(),
});

export type BulkDonorScheduleInput = z.infer<typeof BulkDonorScheduleRowSchema>;

export const BulkDonorScheduleRowsSchema = z.array(
  BulkDonorScheduleRowSchema.optional(),
);

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

export const BulkDonorScheduleFieldsSchema = z.object({
  aspirationDate: z.date().min(startOfToday()),
  fertilizationSiteId: z.string().min(1),
  aspirationSiteId: z.string().min(1),
});

export type BulkDonorScheduleFieldsInput = z.infer<
  typeof BulkDonorScheduleFieldsSchema
>;
